import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAffiliatesApi } from '@/hooks/useAffiliatesApi';
import { formatDateShort } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Loader2, Medal, Monitor, Trophy } from 'lucide-react';
import { useMemo, useState } from 'react';

export function AffiliateLeaderboardContent() {
  const affiliatesApi = useAffiliatesApi();
  const { activeProfile } = useAuthStore();
  const affiliateId = activeProfile?.affiliate_id || '';
  const [selectedWorkoutId, setSelectedWorkoutId] = useState<string>('');

  // Fetch programming to get workout options
  const { data: programmingData } = useQuery({
    queryKey: ['affiliate-programming-list'],
    queryFn: async () => {
      const response = await affiliatesApi.getProgramming();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  // Get today's workout by default
  const todayStr = new Date().toISOString().split('T')[0];
  const workoutOptions = useMemo(() => {
    const workouts = programmingData?.workouts || [];
    return workouts
      .filter((w) => w.scheduled_date) // Only include workouts with valid dates
      .map((w) => ({
        value: w.id,
        label: `${w.name} (${formatDateShort(w.scheduled_date)})`,
      }));
  }, [programmingData?.workouts]);

  // Set default workout to today's or first available
  const effectiveWorkoutId = useMemo(() => {
    if (selectedWorkoutId) return selectedWorkoutId;
    const todayWorkout = programmingData?.workouts?.find(
      (w) => w.scheduled_date?.split('T')[0] === todayStr
    );
    return todayWorkout?.id || programmingData?.workouts?.[0]?.id || '';
  }, [selectedWorkoutId, programmingData?.workouts, todayStr]);

  const {
    data: leaderboardData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['affiliate-leaderboard', effectiveWorkoutId],
    queryFn: async () => {
      if (!effectiveWorkoutId) return { leaderboard: [] };
      const response = await affiliatesApi.getLeaderboard(effectiveWorkoutId);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    enabled: !!effectiveWorkoutId,
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  const handleTVDisplay = () => {
    if (effectiveWorkoutId) {
      window.open(`/tv/${effectiveWorkoutId}`, '_blank');
    }
  };

  const handleLeaderboardTV = () => {
    if (effectiveWorkoutId && affiliateId) {
      window.open(
        `/tv/leaderboard?workout_id=${effectiveWorkoutId}&affiliate_id=${affiliateId}`,
        '_blank'
      );
    }
  };

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'text-yellow-500';
      case 2:
        return 'text-gray-400';
      case 3:
        return 'text-amber-600';
      default:
        return 'text-muted-foreground';
    }
  };

  const getRankIcon = (rank: number) => {
    if (rank <= 3) {
      return <Trophy className={`h-5 w-5 ${getRankColor(rank)}`} />;
    }
    return <span className="w-5 text-center">{rank}</span>;
  };

  const formatScore = (value: number | undefined, unit: string | undefined) => {
    if (value == null) return '-';
    if (unit === 'time' || unit === 'seconds') {
      const mins = Math.floor(value / 60);
      const secs = value % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return unit ? `${value} ${unit}` : String(value);
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
        <p className="text-destructive">Failed to load leaderboard</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">Leaderboard</h1>
          <p className="mt-1 text-muted-foreground">
            See who's at the top and launch TV display mode.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleTVDisplay} disabled={!effectiveWorkoutId}>
            <Monitor className="h-4 w-4 mr-2" />
            Workout TV
            <ExternalLink className="h-3 w-3 ml-2" />
          </Button>
          <Button onClick={handleLeaderboardTV} disabled={!effectiveWorkoutId || !affiliateId}>
            <Trophy className="h-4 w-4 mr-2" />
            Leaderboard TV
            <ExternalLink className="h-3 w-3 ml-2" />
          </Button>
        </div>
      </div>

      {/* Workout Selector */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <label htmlFor="workout-select" className="text-sm font-medium">
              Select Workout:
            </label>
            <div className="flex-1 max-w-sm">
              <Select
                id="workout-select"
                options={[{ value: '', label: 'Select a track...' }, ...workoutOptions]}
                value={selectedWorkoutId || effectiveWorkoutId}
                onChange={(e) => setSelectedWorkoutId(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Medal className="h-5 w-5 text-primary" />
            Rankings
            <Badge variant="outline" className="ml-2">
              Auto-refreshes every 30s
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!leaderboardData?.leaderboard || leaderboardData.leaderboard.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Medal className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">No scores yet</h3>
              <p className="text-muted-foreground max-w-md">
                {effectiveWorkoutId
                  ? 'No members have logged scores for this workout yet.'
                  : 'Select a track to view the leaderboard.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Rank</TableHead>
                  <TableHead>Athlete</TableHead>
                  <TableHead className="text-right">Score</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaderboardData.leaderboard.map((entry, index) => (
                  <TableRow
                    key={entry.user_id}
                    className={`animate-fade-in ${entry.rank <= 3 ? 'bg-primary/5' : ''}`}
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <TableCell>
                      <div className="flex items-center justify-center">
                        {getRankIcon(entry.rank)}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{entry.user_name}</TableCell>
                    <TableCell className="text-right">
                      <span
                        className={`font-mono font-semibold ${
                          entry.rank === 1 ? 'text-primary' : ''
                        }`}
                      >
                        {formatScore(entry.score_value, entry.score_unit)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AffiliateLeaderboard() {
  return (
    <DashboardLayout>
      <AffiliateLeaderboardContent />
    </DashboardLayout>
  );
}
